import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from '@/stores/useSuiteStore'

export type NewsType = 'CHANGELOG' | 'FEATURE' | 'INFO' | 'ALERT'
export type NewsStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED'

export interface NewsItem {
  uuid: string
  type: NewsType
  status: NewsStatus
  title: string
  publishedAt: string | null
  dispatchedAt: string | null
  readCount: number
  createdAt: string
  updatedAt: string
}

export interface NewsDetail extends NewsItem {
  body: string
  authorUuid?: string | null
}

export interface CreateNewsPayload {
  type?: NewsType
  title: string
  body: string
}

export type UpdateNewsPayload = {
  type?: NewsType
  title?: string
  body?: string
}

export interface ListNewsQuery {
  status?: NewsStatus
  type?: NewsType
}

export interface PublishNewsValidationError {
  code: string
  message: string
}

export interface PublishNewsResult {
  ok: boolean
  errors?: PublishNewsValidationError[]
  news?: NewsItem
}

type StoreResult<T> = { ok: true; data: T } | { ok: false; message: string }

async function readError(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json()
    if (body?.message) return Array.isArray(body.message) ? body.message.join(', ') : String(body.message)
  } catch {
    // ignore
  }
  return fallback
}

function buildQueryString(query: ListNewsQuery): string {
  const params = new URLSearchParams()
  if (query.status) params.append('status', query.status)
  if (query.type) params.append('type', query.type)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export const useNewsAdminStore = defineStore('newsAdmin', () => {
  const suiteStore = useSuiteStore()
  const items = ref<NewsItem[]>([])
  const isLoading = ref(false)

  async function fetchAll(query: ListNewsQuery = {}): Promise<NewsItem[]> {
    isLoading.value = true
    try {
      const res = await suiteStore.suiteFetch(`admin/news${buildQueryString(query)}`, {
        method: 'GET',
        credentials: 'include',
      })
      if (res && typeof res !== 'boolean' && res.ok) {
        items.value = (await res.json()) as NewsItem[]
        return items.value
      }
    } catch (e) {
      console.error('Erreur récupération des news', e)
    } finally {
      isLoading.value = false
    }
    return []
  }

  async function fetchDetail(uuid: string): Promise<NewsDetail | null> {
    const res = await suiteStore.suiteFetch(`admin/news/${uuid}`, {
      method: 'GET',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      return (await res.json()) as NewsDetail
    }
    return null
  }

  async function create(payload: CreateNewsPayload): Promise<StoreResult<NewsDetail>> {
    const res = await suiteStore.suiteFetch('admin/news', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const data = (await res.json()) as NewsDetail
      await fetchAll()
      return { ok: true, data }
    }
    const message = res && typeof res !== 'boolean'
      ? await readError(res, 'Erreur lors de la création')
      : 'Erreur lors de la création'
    return { ok: false, message }
  }

  async function update(uuid: string, payload: UpdateNewsPayload): Promise<StoreResult<NewsDetail>> {
    const res = await suiteStore.suiteFetch(`admin/news/${uuid}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const data = (await res.json()) as NewsDetail
      await fetchAll()
      return { ok: true, data }
    }
    const message = res && typeof res !== 'boolean'
      ? await readError(res, 'Erreur lors de la mise à jour')
      : 'Erreur lors de la mise à jour'
    return { ok: false, message }
  }

  /** Publie immédiatement (publishedAt absent) ou programme (date ISO future). */
  async function publish(uuid: string, publishedAt?: string): Promise<StoreResult<PublishNewsResult>> {
    const res = await suiteStore.suiteFetch(`admin/news/${uuid}/publish`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(publishedAt ? { publishedAt } : {}),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const data = (await res.json()) as PublishNewsResult
      if (data.ok) await fetchAll()
      return { ok: true, data }
    }
    const message = res && typeof res !== 'boolean'
      ? await readError(res, 'Erreur lors de la publication')
      : 'Erreur lors de la publication'
    return { ok: false, message }
  }

  async function unpublish(uuid: string): Promise<StoreResult<NewsItem>> {
    const res = await suiteStore.suiteFetch(`admin/news/${uuid}/unpublish`, {
      method: 'POST',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const data = (await res.json()) as NewsItem
      await fetchAll()
      return { ok: true, data }
    }
    const message = res && typeof res !== 'boolean'
      ? await readError(res, 'Erreur lors de la dépublication')
      : 'Erreur lors de la dépublication'
    return { ok: false, message }
  }

  async function archive(uuid: string): Promise<StoreResult<NewsItem>> {
    const res = await suiteStore.suiteFetch(`admin/news/${uuid}/archive`, {
      method: 'POST',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const data = (await res.json()) as NewsItem
      await fetchAll()
      return { ok: true, data }
    }
    const message = res && typeof res !== 'boolean'
      ? await readError(res, "Erreur lors de l'archivage")
      : "Erreur lors de l'archivage"
    return { ok: false, message }
  }

  return {
    items,
    isLoading,
    fetchAll,
    fetchDetail,
    create,
    update,
    publish,
    unpublish,
    archive,
  }
})
